import ApiCollection                from '@widesk-core/models/ApiCollection';
import PagedCollection             from '@widesk-core/models/PagedCollection';
import ApiModel                    from '@widesk-core/models/ApiModel';
import { getLocale }               from '@widesk-core/locale';
import { observable }              from 'mobx';
import { runInAction }             from 'mobx';
import { when }                    from 'mobx';
import { FunctionalityConfig }     from './types';
import { FunctionalityEntry }      from './types';
import { FunctionalityLimitEntry } from './types';
import { FunctionalityUnit }       from './types';
import { toLimit }                 from './types';

/**
 * Implémentation du store : les lectures déclenchent les fetchs nécessaires et retournent
 * `undefined` tant que la donnée n'est pas chargée (réactif via mobx). Les caches sont
 * clefés par account (et par contexte) : un changement d'account repart de zéro.
 */

type LooseQuery = { reference: string; context?: ApiModel };

const WAIT_TIMEOUT_MS = 15_000;

type ValueModel = ApiModel & { value?: string };

type ReferentialModel = ApiModel & { reference: string; title?: string };

/** Une fonctionnalité et la valeur que lui donne un abonnement ou un plan. */
type Value = { reference: string; title: string; value: string };

type Values = { isLoaded: boolean; values: Value[] };

type Change = { from: number; reference: string; title: string; to: number; usage?: number };

const emptyComparison = (isLoaded: boolean) => ({
	exceededLimits: [] as Change[],
	gainedFeatures: [] as Value[],
	increasedLimits: [] as Change[],
	isLoaded,
	keptFeatures: [] as Value[],
	keptLimits: [] as Change[],
	lostFeatures: [] as Value[],
	reducedLimits: [] as Change[],
});

export default class FunctionalityStore {
	private _subscriptionValues = observable.map<string, PagedCollection<ValueModel>>();

	private _usages = observable.map<string, PagedCollection<ApiModel>>();

	private readonly _usageQueries = new Map<string, LooseQuery>();

	/** Valeurs d'un abonnement donné, lues en bloc : une collection par abonnement consulté. */
	private _accountValues = observable.map<string, ApiCollection<ValueModel>>();

	/** Valeurs de tous les plans du catalogue : une seule collection, filtrée à la lecture. */
	private readonly _planValues: ApiCollection<ValueModel>;

	/** Référentiel des fonctionnalités : leurs libellés, dans la langue du moment. */
	private readonly _referential: ApiCollection<ReferentialModel>;

	private _referentialLocale?: string;

	constructor(private readonly _config: FunctionalityConfig<Record<string, FunctionalityEntry>>) {
		this._planValues = new ApiCollection(_config.planValueModel);
		this._referential = new ApiCollection(_config.referentialModel);
	}

	public isEnabled(reference: string): boolean | undefined {
		const collection = this.subscriptionValueCollection(reference);

		if (!collection?.isLoaded) return undefined;

		const model = collection.models[0];

		return !!model && model.value === '1';
	}

	public isAvailable(reference: string): boolean | undefined {
		const collection = this.subscriptionValueCollection(reference);

		if (!collection?.isLoaded) return undefined;

		const model = collection.models[0];

		return !!model && this.isIncluded(reference, model.value ?? '');
	}

	public limit(reference: string): number | undefined {
		const collection = this.subscriptionValueCollection(reference);

		if (!collection?.isLoaded) return undefined;

		return toLimit(collection.models[0]?.value);
	}

	public usage(query: LooseQuery): number | undefined {
		const collection = this.usageCollection(query);

		return collection?.isLoaded ? collection.total : undefined;
	}

	public isLimitReached(query: LooseQuery): boolean | undefined {
		const limit = this.limit(query.reference);
		const usage = this.usage(query);

		if (limit === undefined || usage === undefined) return undefined;

		return usage >= limit;
	}

	public async whenEnabled(reference: string): Promise<boolean> {
		await this.readSubscriptionValue(reference);

		return this.isEnabled(reference)!;
	}

	public async whenAvailable(reference: string): Promise<boolean> {
		await this.readSubscriptionValue(reference);

		return this.isAvailable(reference)!;
	}

	public async whenLimitReached(query: LooseQuery): Promise<boolean> {
		await Promise.all([
			this.readSubscriptionValue(query.reference),
			this.readUsage(query),
		]);

		return this.isLimitReached(query)!;
	}

	public canCount(query: LooseQuery) {
		return !!this._config.getAccount()?.id && !!this.entry(query.reference).model() && !!this.usageFilters(query);
	}

	public refreshSubscriptionValue(reference: string) {
		const accountId = this._config.getAccount()?.id;

		if (!accountId) return;

		const collection = this._subscriptionValues.get(`${accountId}:${reference}`);

		// Pas encore en cache : la création déclenche déjà un list().
		if (!collection) {
			this.ensureSubscriptionValueCollection(reference);
			return;
		}

		if (!collection.isLoading) collection.list();
	}

	public refreshUsage(query: LooseQuery) {
		const collection = this._usages.get(this.usageKey(query));

		if (!collection) {
			this.ensureUsageCollection(query);
			return;
		}

		const filters = this.usageFilters(query);

		if (!filters) return;

		collection.setFilters(filters);
		collection.list();
	}

	public refreshUsagesOf(reference: string) {
		const prefix = `${this._config.getAccount()?.id}:${reference}:`;

		[...this._usageQueries.entries()]
			.filter(([key]) => key.startsWith(prefix))
			.forEach(([, query]) => this.refreshUsage(query));
	}

	public isFeature(reference: string) {
		return this._config.functionalities[reference]?.type === 'feature';
	}

	public scopeOf(reference: string): string | undefined {
		const entry = this._config.functionalities[reference];

		return entry?.type === 'limit' ? entry.scope : undefined;
	}

	public unitOf(reference: string): FunctionalityUnit | undefined {
		const entry = this._config.functionalities[reference];

		return entry?.type === 'feature' ? undefined : entry?.unit;
	}

	/** Une feature vaut '1' quand elle est activée ; une limite est incluse dès qu'elle autorise plus de zéro. */
	public isIncluded(reference: string, value: string) {
		return this.isFeature(reference) ? value === '1' : toLimit(value) > 0;
	}

	// Lecture pure : charger d'abord avec loadReferential.
	public titleOf(reference: string) {
		return this._referential.find(item => item.reference === reference)?.title || undefined;
	}

	// Lecture pure : charger d'abord avec loadAccountValues.
	public accountValues(accountSubscriptionId: id): Values {
		return this.toValues(this._accountValues.get(String(accountSubscriptionId)));
	}

	// Lecture pure : charger d'abord avec loadPlanValues.
	public planValues(planId: id): Values {
		const values = this.toValues(this._planValues);

		if (!values.isLoaded) return values;

		const rows = this._planValues.models.filter(row => String(row.get('subscription.id')) === String(planId));

		return { isLoaded: true, values: this.toValueList(rows) };
	}

	public compare(from: Values, to: Values) {
		const comparison = emptyComparison(from.isLoaded && to.isLoaded);

		if (!comparison.isLoaded) return comparison;

		from.values.forEach(current => {
			const target = to.values.find(value => value.reference === current.reference);

			if (this.isFeature(current.reference)) {
				const wasIncluded = this.isIncluded(current.reference, current.value);
				const isIncluded = !!target && this.isIncluded(target.reference, target.value);

				if (wasIncluded && !isIncluded) comparison.lostFeatures.push(current);
				else if (!wasIncluded && isIncluded) comparison.gainedFeatures.push(current);
				else if (wasIncluded) comparison.keptFeatures.push(current);

				return;
			}

			const change: Change = {
				from: toLimit(current.value),
				reference: current.reference,
				title: current.title,
				to: toLimit(target?.value),
				usage: this.accountScopedUsage(current.reference),
			};

			if (change.to < change.from) {
				comparison.reducedLimits.push(change);

				// Ce que le compte utilise déjà ne disparaît pas, mais il ne pourra plus rien créer.
				if (change.usage !== undefined && change.usage > change.to) {
					comparison.exceededLimits.push(change);
				}
			} else if (change.to > change.from) {
				comparison.increasedLimits.push(change);
			} else if (change.from > 0) {
				comparison.keptLimits.push(change);
			}
		});

		return comparison;
	}

	public loadAccountValues(accountSubscriptionId: id) {
		this.loadReferential();

		const key = String(accountSubscriptionId);

		if (this._accountValues.has(key)) return;

		const created = new ApiCollection(this._config.accountValueModel);
		runInAction(() => this._accountValues.set(key, created));
		created.setFilters({ accountSubscription: accountSubscriptionId } as never);
		created.list();
	}

	/** Après un changement de plan, les valeurs du compte ne sont plus celles qu'on a lues. */
	public refreshAccountValues(accountSubscriptionId: id) {
		const collection = this._accountValues.get(String(accountSubscriptionId));

		if (!collection) {
			this.loadAccountValues(accountSubscriptionId);
			return;
		}

		collection.list();
	}

	public loadPlanValues() {
		this.loadReferential();

		if (this._planValues.isLoading || this._planValues.isLoaded) return;

		this._planValues.list();
	}

	/** Les limites de scope account sont les seules comptables sans contexte : elles suffisent à l'écran. */
	public loadAccountUsages() {
		Object.keys(this._config.functionalities)
			.filter(reference => this.scopeOf(reference) === 'account')
			.forEach(reference => this.refreshUsage({ reference }));
	}

	public loadReferential() {
		const locale = getLocale();

		// Déjà chargé dans cette langue : rien à refaire. Un échec remet le compteur à zéro pour
		// que l'écran suivant retente.
		if (this._referentialLocale === locale) return;

		this._referentialLocale = locale;

		this._referential.setLocale(locale).list().catch(() => {
			this._referentialLocale = undefined;
		});
	}

	private accountScopedUsage(reference: string) {
		return this.scopeOf(reference) === 'account' ? this.usage({ reference }) : undefined;
	}

	private toValues(collection?: ApiCollection<ValueModel>): Values {
		if (!collection?.isLoaded || !this._referential.isLoaded) return { isLoaded: false, values: [] };

		return { isLoaded: true, values: this.toValueList(collection.models) };
	}

	private toValueList(rows: ValueModel[]): Value[] {
		return rows
			.map(row => {
				const functionality = this._referential.find(
					item => String(item.id) === String(row.get('functionality.id')),
				);

				if (!functionality) return undefined;

				return {
					reference: functionality.reference,
					title: functionality.title || functionality.reference,
					value: row.value ?? '',
				};
			})
			.filter((value): value is Value => !!value);
	}

	// Lecture pure : ne doit jamais muter `_subscriptionValues`, appelée pendant le render des Gates.
	private subscriptionValueCollection(reference: string) {
		const accountId = this._config.getAccount()?.id;

		if (!accountId) return undefined;

		return this._subscriptionValues.get(`${accountId}:${reference}`);
	}

	// Création + fetch si absent : ne doit être appelée que hors render (effets, refresh impératifs).
	private ensureSubscriptionValueCollection(reference: string) {
		const accountId = this._config.getAccount()?.id;

		if (!accountId) return undefined;

		const key = `${accountId}:${reference}`;
		let collection = this._subscriptionValues.get(key);

		if (!collection) {
			const created = new PagedCollection(this._config.accountValueModel);
			collection = created;
			runInAction(() => this._subscriptionValues.set(key, created));
			created.setItemsPerPage(1);
			created.setFilters({
				'functionality.reference': reference,
				'accountSubscription.account': accountId,
				'accountSubscription.accountSubscriptionStatus': '1',
			} as never);
			created.list();
		}

		return collection;
	}

	private async readSubscriptionValue(reference: string) {
		await when(() => !!this._config.getAccount()?.id, { timeout: WAIT_TIMEOUT_MS });

		const existing = this.subscriptionValueCollection(reference);

		if (existing) {
			await existing.list();
			return;
		}

		const created = this.ensureSubscriptionValueCollection(reference);

		await when(() => !!created?.isLoaded, { timeout: WAIT_TIMEOUT_MS });
	}

	/** Recompte l'usage et attend la réponse : une action ne se décide pas sur un comptage périmé. */
	private async readUsage(query: LooseQuery) {
		await when(() => this.canCount(query), { timeout: WAIT_TIMEOUT_MS });

		const existing = this.usageCollection(query);

		if (existing) {
			existing.setFilters(this.usageFilters(query)!);
			await existing.list();
			return;
		}

		const created = this.ensureUsageCollection(query);

		await when(() => !!created?.isLoaded, { timeout: WAIT_TIMEOUT_MS });
	}

	private entry(reference: string) {
		return this._config.functionalities[reference] as FunctionalityLimitEntry;
	}

	private usageFilters(query: LooseQuery) {
		const entry = this.entry(query.reference);

		// Scope account : le contexte est ambiant, c'est l'account courant.
		const context = entry.scope === 'account' ? this._config.getAccount() : query.context;

		if (!context) return undefined;

		// Le typage de FunctionalityQueryOf garantit que le contexte correspond
		// à la signature `filters` de l'entrée.
		return entry.filters(context as never);
	}

	// Lecture pure : ne doit jamais muter `_usages`, appelée pendant le render des Gates.
	private usageCollection(query: LooseQuery) {
		if (!this._config.getAccount()?.id) return undefined;

		return this._usages.get(this.usageKey(query));
	}

	// Création + fetch si absent : ne doit être appelée que hors render (effets, refresh impératifs).
	private ensureUsageCollection(query: LooseQuery) {
		const entry = this.entry(query.reference);
		const model = entry.model();
		const filters = this.usageFilters(query);

		// Classe de modèle pas encore chargée (import() dynamique du dictionnaire)
		// ou filtres pas encore prêts (account absent…) : on réessaiera à la prochaine lecture.
		if (!model || !filters || !this._config.getAccount()?.id) return undefined;

		const key = this.usageKey(query);
		let collection = this._usages.get(key);

		if (!collection) {
			const created = new PagedCollection(model);
			collection = created;
			this._usageQueries.set(key, query);
			runInAction(() => this._usages.set(key, created));
			created.setItemsPerPage(0);
			created.setFilters(filters);
			created.list();
		}

		return collection;
	}

	private usageKey(query: LooseQuery) {
		return `${this._config.getAccount()?.id}:${query.reference}:${query.context?.id ?? ''}`;
	}
}
